<script setup lang="ts">
/**
 * FICHE PRODUIT DÉDIÉE — `/boutique/{slug}/{productSlug}`.
 *
 * ⚠️ POURQUOI UNE PAGE ET PAS UN MODAL (décision validée, chantier boutique).
 * Une association ne vend pas depuis une vitrine visitée spontanément : elle
 * colle un lien dans un groupe WhatsApp, sur Facebook, dans sa newsletter. Un
 * produit sans URL propre est donc littéralement invendable — on ne peut ni le
 * partager, ni l'indexer, ni le pousser en publicité. Baymard documente en
 * outre que les testeurs confondent un « quick view » avec la vraie fiche et
 * s'attendent à y trouver TOUTE l'information d'achat.
 *
 * L'identifiant d'URL est DÉRIVÉ (`shopProductUrl.ts`) : `{nom}-{8 car. d'id}`.
 * Aucune migration, et le lien partagé survit à un renommage du produit —
 * seul le suffixe d'id sert à la résolution.
 *
 * Le panier est celui de la vitrine (`useShopCart`, sessionStorage par
 * boutique) : ajouter ici puis revenir au tunnel conserve la sélection.
 */
import type { Event } from "~/types/event";
import type { ShopProduct, ShopProductVariant } from "~/types/shop";
import { resolveShopProductFromSlug, shopProductPath } from "~/utils/shopProductUrl";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const { t } = useI18n();

const { sanitize } = useSanitizeHtml();

definePageMeta({ layout: false });

const route = useRoute();
const routeParams = computed(
  () => route.params as Record<string, string | string[] | undefined>,
);
const slug = computed(() => routeParams.value.slug as string);
const productSlug = computed(() => routeParams.value.productSlug as string);
const config = useRuntimeConfig();

// Mode widget embarqué (?embed=1) : masque l'habillage, fond transparent,
// auto-resize vers l'hôte. La navigation reste INTERNE à l'iframe (NuxtLink /
// navigateTo), donc le tunnel embarqué n'est jamais cassé.
const embedRootEl = ref<HTMLElement | null>(null);
const { isEmbed, hostOrigin } = useEmbedFrame(embedRootEl);

// Fetch SSR + disponibilité — MÊME composable/clé que la vitrine : revenir à
// la boutique ne redéclenche aucun appel réseau.
const isPreviewQuery = computed(() => route.query.preview === "true");
const { shop, isInitialLoading, isEntityMissing, missingCopy } =
  await useShopPublicData(slug, isPreviewQuery);

// Accent color (réutilise le composable /b — seule accent_color est lue).
const { accentStyle } = useEventAccentColor(shop as unknown as Ref<Event | null>);

// Socle commun aux trois pages boutique : fenêtre de vente, message de
// clôture, query d'habillage à propager (embed / aperçu).
const {
  isPreview,
  carryQuery,
  canBuy: saleWindowOpen,
  saleClosedMessage,
  showRemainingStock,
} = useShopPageContext(shop);

// ─── Panier (partagé avec la vitrine via sessionStorage) ───────────
const {
  cartLines,
  products,
  totalItems,
  subtotal,
  getMaxForLine,
  addLine,
  setLineQuantity,
  removeLine,
} = useShopCart(slug, shop);

/** Produit désigné par l'URL — résolu côté client ET SSR. */
const product = computed<ShopProduct | null>(() =>
  resolveShopProductFromSlug(products.value, productSlug.value),
);

/** Produit introuvable (ou boutique absente) → vrai 404. */
const isProductMissing = computed(
  () => !isInitialLoading.value && !!shop.value && !product.value,
);

// Vrai statut HTTP 404 côté SSR (boutique OU produit inexistant) : sans ça,
// un lien mort partagé renverrait un 200 et polluerait l'index.
if (import.meta.server && (isEntityMissing.value || isProductMissing.value)) {
  const requestEvent = useRequestEvent();
  if (requestEvent) setResponseStatus(requestEvent, 404);
}

// SEO + JSON-LD Product/Offer + OG (pattern useShopCheckoutSeo).
useShopProductSeo(shop, product, slug);

/**
 * Commande réellement possible : fenêtre de vente ouverte ET association
 * capable d'encaisser. Miroir EXACT de la vitrine — un produit reste
 * consultable dans tous les cas, seul l'achat est bloqué.
 */
const { paymentProviders } = usePaymentProvidersState(shop);
const canBuy = computed(
  () => saleWindowOpen.value && paymentProviders.value.accepts_payments === true,
);

// ─── Temps réel stock (même room que la vitrine) ───────────────────
// `checkoutStep` figé à 1 : il n'y a pas de tunnel ici, le composable ne s'en
// sert que pour ramener l'acheteur au panier — sans objet sur une fiche.
const staticCheckoutStep = ref(1);
useShopSocket({ shop, totalItems, checkoutStep: staticCheckoutStep });
// Dernier achat (moins de 7 jours) annoncé à l'arrivée (Karim 2026-10-10).
useAnnonceDerniereActivite(
  () => shop.value?.derniere_activite,
  (_a, recente) => t(recente ? "commun.enDirect.vientDAcheter" : "commun.enDirect.aAchete"),
  "i-lucide-shopping-bag",
);

// ─── Galerie ───────────────────────────────────────────────────────
const assetsBaseUrl = computed(
  () => (config.public.apiUrl as string) || "https://api.donoor.org",
);
const imageIds = computed(() => product.value?.images || []);
const currentImageIndex = ref(0);
watch(product, () => { currentImageIndex.value = 0; });

// Les vignettes des « autres produits » sont désormais construites par
// [[BoutiqueRelatedProducts]] (qui reçoit `directusUrl`) : plus aucun helper
// d'URL de vignette n'est nécessaire ici, seul le grand format de la galerie.
const fullImages = computed(() =>
  imageIds.value.map(
    (id) => `${assetsBaseUrl.value}/api/assets/${id}?width=1600&format=webp&quality=90`,
  ),
);

const descriptionHtml = computed(() =>
  product.value?.description ? sanitize(product.value.description) : null,
);

/**
 * Rubriques libres (migration 169) — « Composition », « Entretien »…
 * Le contenu est du HTML riche saisi par l'association : il est sanitizé ICI,
 * au même endroit et avec le même helper que la description, pour que le
 * composant d'affichage n'ait JAMAIS à manipuler du HTML non nettoyé.
 */
const productSections = computed(() =>
  (product.value?.sections ?? []).map((section) => ({
    title: section.title,
    content: sanitize(section.content),
  })),
);

// ─── Livraison / retrait & CGV (sources des panneaux dépliants) ────
const shippingMethods = computed(() => shop.value?.shipping_methods || []);
const termsUrl = computed(() =>
  shop.value?.terms_file_id
    ? `${assetsBaseUrl.value}/api/assets/${shop.value.terms_file_id}`
    : null,
);

// ─── Navigation ────────────────────────────────────────────────────
const shopLink = computed(() => ({
  path: `/boutique/${slug.value}`,
  query: carryQuery.value,
}));

/** Nom affiché du vendeur (repli neutre si l'API ne l'a pas enrichi). */
const organizationLabel = computed(
  () => shop.value?.organization_name || t("boutique.commun.associationGenerique"),
);

/**
 * Page publique de l'association — `/associations/{slug}`.
 * `null` si `organization_slug` est absent (champ OPTIONNEL de l'API) : mieux
 * vaut un nom en texte simple qu'un lien vers un 404.
 */
const organizationUrl = computed<string | null>(() =>
  shop.value?.organization_slug
    ? `/associations/${shop.value.organization_slug}`
    : null,
);

function productLink(p: ShopProduct) {
  return { path: shopProductPath(slug.value, p), query: carryQuery.value };
}

/** Autres produits de la boutique (rebond, max 4) — jamais celui affiché. */
const otherProducts = computed(() =>
  products.value.filter((p) => p.id !== product.value?.id).slice(0, 4),
);

// ─── Panneau latéral panier ────────────────────────────────────────
const { open: openCartDrawer } = useShopCartDrawer();

/** Page de commande — destination de « Commander » dans le panneau. */
const cartPageLink = computed(() => ({
  path: `/boutique/${slug.value}/panier`,
  query: carryQuery.value,
}));

// ─── Ajout au panier ───────────────────────────────────────────────
const toast = useToast();

interface ShopAddPayload {
  product: ShopProduct;
  variant: ShopProductVariant | null;
  quantity: number;
  custom_price?: number;
  custom_data?: Record<string, unknown>;
}

/**
 * Ajout au panier COMMUN aux deux actions du panneau d'achat. Renvoie `true`
 * si la ligne a bien été créée — c'est cette réponse qui autorise (ou non) la
 * suite propre à chaque action. Un échec de plafond ne doit JAMAIS emmener
 * l'acheteur ailleurs : il reste sur la fiche, avec le message qui nomme le
 * restant exact.
 */
function addToCart(payload: ShopAddPayload): boolean {
  // Restant AVANT l'ajout : c'est cette valeur qu'il faut nommer si l'ajout
  // échoue (après, getMaxForLine tient déjà compte de la ligne créée).
  const maxBefore = getMaxForLine(payload.product, payload.variant);
  if (addLine(payload)) return true;
  // Échec = plafond atteint. On NOMME le restant exact plutôt que de laisser
  // l'acheteur deviner (jamais de message sans issue).
  const remaining = Number.isFinite(maxBefore) ? Math.max(0, maxBefore) : 0;
  toast.add({
    title: t("boutique.productPage.quantiteIndisponibleTitre", { article: payload.product.name }),
    description:
      remaining > 0
        ? t("boutique.productPage.exemplairesDisponiblesAjuster", { remaining }, remaining)
        : t("boutique.productPage.articleIndisponibleMax"),
    color: "warning",
    duration: 6000,
  });
  return false;
}

/**
 * « Ajouter au panier » — l'acheteur CONTINUE ses achats.
 * OUVERTURE AUTOMATIQUE DU PANNEAU : avant, un ajout depuis cette page ne
 * produisait qu'un toast — l'acheteur ne voyait ni son panier, ni son total, et
 * devait retourner sur la vitrine pour les trouver. Le panneau montre les deux,
 * sur place, sans quitter la fiche.
 */
function onAddToCart(payload: ShopAddPayload) {
  if (addToCart(payload)) openCartDrawer(null);
}

/**
 * « Acheter maintenant » — raccourci de l'acheteur d'UN SEUL article : on
 * ajoute puis on file droit à la page panier, SANS ouvrir le panneau latéral
 * (qui ne ferait qu'ajouter un clic sur le chemin qu'il vient de demander).
 *
 * `cartPageLink` porte déjà `carryQuery` : l'habillage embed / aperçu / host
 * survit donc à la navigation, exactement comme pour « Commander ». Vaut aussi
 * pour un produit NUMÉRIQUE : sans variantes ni stock, la sélection est valide
 * d'emblée et l'acheteur atteint le paiement en un clic.
 */
function onBuyNow(payload: ShopAddPayload) {
  if (addToCart(payload)) void navigateTo(cartPageLink.value);
}
</script>

<template>
  <div
    ref="embedRootEl"
    :class="isEmbed
      ? 'bg-transparent @container'
      : ((!isInitialLoading && (isEntityMissing || isProductMissing)) ? 'min-h-screen flex flex-col bg-white dark:bg-[#0a0a0a]' : 'min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]')"
    :style="accentStyle"
  >
    <!-- Surveillance : asso suspended/banned -->
    <template v-if="shop && (shop.organization_risk_status === 'suspended' || shop.organization_risk_status === 'banned')">
      <AppHeader v-if="!isEmbed" shop-mode />
      <OrgUnavailable
        :risk-status="shop.organization_risk_status"
        :reason="shop.organization_risk_reason"
        :organization-name="shop.organization_name"
      />
    </template>

    <PublicPageLoadingSkeleton v-else-if="isInitialLoading || (!shop && !isEntityMissing)" />

    <PublicPageErrorState
      v-else-if="isEntityMissing"
      :icon="missingCopy.icon"
      :title="missingCopy.title"
      :message="missingCopy.message"
    >
      <template #suggestions>
        <NotFoundSuggestions type="associations" />
      </template>
    </PublicPageErrorState>

    <!-- Produit retiré / lien erroné : on RENVOIE vers la boutique, qui est
         vivante, plutôt que sur un cul-de-sac. -->
    <template v-else-if="isProductMissing">
      <AppHeader v-if="!isEmbed" :links="navLinks" shop-mode :cart-count="totalItems" />
      <PublicPageErrorState
        icon="i-lucide-package-x"
        :title="$t('boutique.productPage.produitIntrouvableTitre')"
        :message="$t('boutique.productPage.produitIntrouvableMessage')"
      >
        <template #suggestions>
          <div class="flex justify-center">
            <NuxtLink
              :to="shopLink"
              class="inline-flex items-center gap-2 px-5 min-h-11 accent-btn rounded-xl text-sm font-semibold"
            >
              <UIcon name="i-lucide-store" class="size-4" />
              {{ $t("boutique.productPage.voirLaBoutique") }}
            </NuxtLink>
          </div>
        </template>
      </PublicPageErrorState>
      <AppFooter v-if="!isEmbed" class="mt-auto" />
    </template>

    <template v-else-if="shop && product">
      <AppHeader v-if="!isEmbed" :links="navLinks" shop-mode :cart-count="totalItems" />

      <div v-if="isPreview" class="bg-amber-500 text-white text-center text-sm font-medium py-2 px-4">
        {{ $t("boutique.index.modeApercu") }}
      </div>

      <div :class="isEmbed ? 'w-full' : 'w-full max-w-[1280px] mx-auto px-3 md:px-6'">
        <!-- Fil d'Ariane + retour explicite (le retour natif du navigateur
             fonctionne de toute façon : c'est une vraie navigation). -->
        <!-- Le AppHeader du site est une pilule FLOTTANTE (`fixed top-5`) : il
             recouvre le haut du viewport. Sans bannière pleine largeur (la
             vitrine en a une, pas la fiche produit), il faut donc dégager la
             hauteur du header — convention du site pour une page sans hero
             (`pt-32` : conformite, terms, privacy, mentions-legales). Réduit à
             `pt-24` sur mobile où la pilule est plus basse et l'espace rare.
             En embed il n'y a pas de header du tout : espacement minimal. -->
        <div
          class="flex items-center justify-between gap-3"
          :class="isEmbed ? 'pt-3' : 'pt-24 md:pt-32'"
        >
          <nav
            :aria-label="$t('boutique.productPage.ariaFilAriane')"
            class="flex items-center gap-1.5 flex-wrap text-xs md:text-sm min-w-0"
          >
            <NuxtLink
              :to="shopLink"
              class="inline-flex items-center gap-1.5 px-2.5 min-h-11 -ml-2.5 rounded-xl text-[#505870] dark:text-gray-300 hover:text-[#2d2e5c] dark:hover:text-white hover:bg-white dark:hover:bg-[#141414] transition-colors font-medium"
            >
              <UIcon name="i-lucide-arrow-left" class="size-4 shrink-0" />
              {{ shop.title }}
            </NuxtLink>
            <UIcon name="i-lucide-chevron-right" class="size-3.5 text-gray-400 shrink-0" />
            <span class="text-[#2d2e5c] dark:text-white font-semibold truncate max-w-[40vw] md:max-w-none">
              {{ product.name }}
            </span>
          </nav>

          <!-- Le panier est FLOTTANT en bas à droite (voir plus bas) : au
               niveau du fil d'Ariane, il défilait hors champ dès la galerie. -->
        </div>

        <div class="flex flex-col wide:flex-row gap-4 md:gap-6 mt-3 md:mt-4">
          <!-- COLONNE GAUCHE : galerie (vignettes verticales à gauche en
               desktop, bande horizontale sous l'image en mobile) PUIS le bloc
               « Détails ». La zone sous la galerie était vide en desktop
               pendant que la colonne d'achat débordait de l'écran : c'est là
               que va le contenu éditorial du produit. -->
          <div class="contents wide:block wide:w-1/2 wide:shrink-0 wide:space-y-6">
            <BoutiqueProductGallery
              v-model="currentImageIndex"
              :image-ids="imageIds"
              :product-name="product.name"
              :directus-url="assetsBaseUrl"
              :full-images="fullImages"
              class="order-1 wide:order-none"
            />

            <!-- Détails : description longue + rubriques libres de l'asso, en
                 clair et à la suite. Disparaît entièrement si le produit n'a ni
                 l'une ni les autres.
                 MOBILE : `contents` sur le conteneur fait remonter galerie et
                 détails au niveau du flex parent en colonne, ce qui permet de
                 les ORDONNER indépendamment — la galerie reste en tête
                 (`order-1`) et les détails passent APRÈS la colonne d'achat
                 (`order-3`). Sans ça, l'acheteur mobile aurait dû franchir
                 toute la fiche technique avant de voir le bouton d'ajout.
                 En `lg`, `contents` est annulé et la colonne redevient une
                 vraie colonne : galerie PUIS détails, l'un sous l'autre. -->
            <BoutiqueProductDetails
              :description-html="descriptionHtml"
              :description-shown-above="false"
              :sections="productSections"
              class="order-3 wide:order-none"
            />
          </div>

          <!-- COLONNE DROITE : identité + achat + livraison.
               `order-2` : en mobile elle se glisse ENTRE la galerie (order-1)
               et les détails (order-3). En `lg` les ordres sont neutralisés et
               la colonne reprend sa place à droite. -->
          <div class="order-2 wide:order-none flex-1 min-w-0 space-y-4 md:space-y-6">
            <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-6">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <!-- « par {asso} » — le lien mène à la PAGE PUBLIQUE de
                       l'association (/associations/{slug}), pas à la boutique
                       où l'on se trouve déjà : c'est la seule destination qui
                       apporte quelque chose depuis cette fiche.
                       Nouvel onglet + `rel="noopener noreferrer"` (obligatoire :
                       sans `noopener`, la page ouverte garde une référence
                       `window.opener` sur celle-ci). En embed, quitter dans un
                       nouvel onglet est justement ce qui PRÉSERVE l'iframe :
                       l'acheteur ne perd pas son panier.
                       `organization_slug` est optionnel côté API : sans lui, on
                       affiche le nom en TEXTE SIMPLE plutôt qu'un lien mort. -->
                  <p class="text-xs text-[#717198] dark:text-gray-400">
                    {{ $t("boutique.commun.par") }}
                    <a
                      v-if="organizationUrl"
                      :href="organizationUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-bold hover:underline"
                      :style="{ color: 'var(--accent, #212121)' }"
                    >{{ organizationLabel }}</a>
                    <span v-else class="font-bold text-[#505870] dark:text-gray-300">{{ organizationLabel }}</span>
                  </p>
                  <h1 class="text-2xl md:text-3xl wide:text-4xl font-bold text-[#2d2e5c] dark:text-white leading-tight mt-2.5 text-balance">
                    {{ product.name }}
                  </h1>
                </div>
                <span
                  v-if="product.category"
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-[#1e1e1e] text-[#505870] dark:text-gray-300 shrink-0"
                >
                  {{ product.category }}
                </span>
              </div>

              <!-- Pas de description ici : elle vit dans le bloc « Détails »
                   sous la galerie (colonne gauche). La répéter au-dessus du
                   prix ferait lire deux fois le même texte sur un écran large,
                   où les deux colonnes sont visibles simultanément. -->

              <div class="mt-5">
                <BoutiqueProductBuyPanel
                  :product="product"
                  :show-remaining-stock="showRemainingStock"
                  :can-buy="canBuy"
                  :sale-closed-message="saleClosedMessage"
                  :get-max-for-line="getMaxForLine"
                  @add="onAddToCart"
                  @buy-now="onBuyNow"
                />
              </div>
            </div>

            <!-- « Comment je le reçois, et à quel prix » : la question qui
                 suit immédiatement le prix, donc collée sous le bouton
                 d'achat. Disparaît si l'asso n'a configuré aucun mode et n'a
                 pas de CGV. -->
            <BoutiqueProductShipping
              :shipping-methods="shippingMethods"
              :terms-url="termsUrl"
            />

            <!-- Rappel du panier après un ajout. « Voir le panier » OUVRE le
                 panneau latéral au lieu de renvoyer sur la vitrine : c'est
                 précisément l'aller-retour qui rendait le parcours pénible.
                 « Commander » mène directement à la page de commande.
                 Le panier est conservé d'une page à l'autre (sessionStorage
                 par boutique, `useShopCart`). -->
            <div
              v-if="totalItems > 0"
              class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-5"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-bold text-[#2d2e5c] dark:text-white">
                    {{ $t("boutique.productPage.articlesDansPanier", { n: totalItems }, totalItems) }}
                  </p>
                  <p class="text-xs text-[#505870] dark:text-gray-400 tabular-nums mt-0.5">
                    {{ $t("boutique.productPage.sousTotal", { montant: subtotal > 0 ? formatCurrency(subtotal) : $t('boutique.commun.gratuit') }) }}
                  </p>
                </div>
                <NuxtLink
                  v-if="canBuy"
                  :to="cartPageLink"
                  data-testid="link-to-cart-page"
                  class="inline-flex items-center gap-2 px-4 min-h-11 accent-btn rounded-xl text-sm font-semibold shrink-0"
                >
                  {{ $t("boutique.commun.commander") }}
                  <UIcon name="i-lucide-arrow-right" class="size-4" />
                </NuxtLink>
              </div>
              <button
                type="button"
                data-testid="btn-view-cart-drawer"
                class="mt-3 inline-flex w-full items-center justify-center gap-2 min-h-11 rounded-xl border border-gray-200 dark:border-[#262626] text-sm font-medium text-[#505870] dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1e1e1e] transition-colors"
                @click="openCartDrawer(null)"
              >
                <UIcon name="i-lucide-shopping-cart" class="size-4" />
                {{ $t("boutique.productPage.voirLePanier") }}
              </button>
            </div>

          </div>
        </div>

        <!-- Rebond vers les autres produits — PLEINE LARGEUR, sous les deux
             colonnes, et SANS carte (le bloc flotte comme la galerie et les
             détails). Il était auparavant coincé au bas de la colonne d'achat,
             où une grille de vignettes n'a ni la place ni le rôle : on ne
             propose un autre produit qu'APRÈS avoir laissé lire celui-ci. -->
        <BoutiqueRelatedProducts
          :products="otherProducts"
          :product-link="productLink"
          :directus-url="assetsBaseUrl"
          class="mt-8 md:mt-12"
        />

        <!-- Paiement sécurisé : HORS des deux colonnes et hors de toute carte.
             La mention ne parle ni du produit ni de la boutique, elle vaut pour
             tout paiement sur Donoor — sa place est en pied de fiche, discrète.
             Masquée en mode widget embarqué, où l'hôte porte déjà son propre
             habillage. -->
        <BoutiqueProductSecurityNote
          v-if="!isEmbed"
          :payment-providers="shop?.payment_providers ?? null"
          class="mt-4 md:mt-6"
        />

        <!-- Embed : « Propulsé par Donoor.org » en bas, centré (Karim 31/08). -->
        <div v-if="isEmbed" class="mt-8 flex justify-center">
          <EmbedPoweredBy type="shop" :host-origin="hostOrigin" />
        </div>

        <!-- Respiration de bas de page, portée ici et non par la grille des
             colonnes : en embed la mention de paiement est masquée, l'espace
             doit rester le même. -->
        <div class="pb-6 md:pb-10" />
      </div>

      <!-- Panier flottant (bas-droite) : il réserve sa place au chat.
           Masqué en EMBED : dans une iframe d'une hauteur de quelques
           centaines de pixels, un élément fixe se colle au milieu du contenu
           de l'hôte. -->
      <BoutiquePanierFlottant v-if="!isEmbed" :total-items="totalItems" />

      <AppFooter v-if="!isEmbed" class="mt-auto" />
    </template>

    <!-- PANNEAU LATÉRAL PANIER — MÊME composant, même état
         (`useShopCartDrawer`) et même panier que sur la vitrine : un seul
         panier, deux points de montage. Il s'ouvre automatiquement à chaque
         ajout depuis cette fiche. -->
    <BoutiqueCartDrawer
      :cart-lines="cartLines"
      :total-items="totalItems"
      :subtotal="subtotal"
      :cart-page-link="cartPageLink"
      :can-buy="canBuy"
      :sale-closed-message="saleClosedMessage"
      :get-max-for-line="getMaxForLine"
      @update-quantity="setLineQuantity"
      @remove-line="removeLine"
    />
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
/* Survol en COULEUR PLEINE (règle projet `ui_no_bg_transparency`) : on
   assombrit l'accent de 12 % au lieu de le rendre translucide, sinon le fond
   de page transparaît à travers le bouton. */
.accent-btn:hover {
  background: color-mix(in srgb, var(--accent, #212121) 88%, #000);
}
</style>
